import { AxleDropzone } from '@/components/formFields/AxleDropzone';
import { AxleSelectField } from '@/components/formFields/AxleSelectField';
import {
    HeadingCaptionLabel,
    LeftRailNoneLabel,
    LeftRailTextButton,
} from '@/components/patients/PatientLeftRail/PatientLeftRail';
import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import {
    ObjectForDocument,
    useUploadDocument,
    useUploadDocumentOptions,
} from '@/fetching/api/documentUpload';
import { DocumentWithLink } from '@/fetching/sharedSchemas/misc';
import { renderDocumentName } from '@/types/document';
import { useStateWithDerivedBoolean } from '@/utils/booleanState';
import { formatDatetime } from '@/utils/datetimes/formatting';
import { useInputState, useJointInputState } from '@/utils/inputState';
import { ReturnComponent } from '@/utils/reactTypes';
import { gql } from '@apollo/client';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import FileIcon from '@mui/icons-material/DescriptionOutlined';
import { Box, Button } from '@mui/material';
import { DocumentType } from 'gql-types';
import _ from 'lodash';
import { useState } from 'react';

import { AxleChip } from './chips';

const nameOfDoc = ({ doc, timezone }: { doc: DocumentWithLink; timezone: string | null }) =>
    `${doc.doc_type_name} @ ${formatDatetime(doc.created_at, timezone)}`;

export const DocumentListItemFragment = gql`
    fragment DocumentListItemFragment on Document {
        id
        documentType
        createdAt
        downloadLink
    }
`;

export interface IDocumentListItem {
    id: string;
    documentType: DocumentType;
    createdAt: Date;
    downloadLink: string;
}

export const DocumentsList = ({
    documents,
    timezone,
}: {
    documents: IDocumentListItem[];
    timezone: string | null;
}): ReturnComponent => {
    return (
        <Box display={'flex'} flexWrap={'wrap'} gap={1}>
            {_.sortBy(documents, 'created_at').map((document) => (
                <AxleChip
                    key={document.id}
                    icon={<AttachFileIcon />}
                    label={renderDocumentName({ document, timezone })}
                    link={document.downloadLink}
                    size='small'
                />
            ))}
        </Box>
    );
};

export const DocumentsListDeprecated = ({
    documents,
    timezone,
}: {
    documents: DocumentWithLink[];
    timezone: string | null;
}): ReturnComponent => {
    return (
        <Box display={'flex'} flexWrap={'wrap'} gap={1}>
            {_.sortBy(documents, 'created_at').map((doc) => (
                <AxleChip
                    key={doc.uuid}
                    icon={<AttachFileIcon />}
                    label={nameOfDoc({ doc, timezone })}
                    link={doc.download_link}
                    size='small'
                />
            ))}
        </Box>
    );
};

export const DocumentViewerAndDropzone = ({
    documents,
    canView,
    canEdit,
    objectType,
    objectId,
    captionLabel,
}: {
    documents: IDocumentListItem[];
    canView: boolean;
    canEdit: boolean;
    objectType: ObjectForDocument['objectType'];
    objectId: ObjectForDocument['objectId'];
    captionLabel: string;
}): ReturnComponent => {
    const [isEditing, setIsEditing] = useState(false);
    const [fileToUpload, setFileToUpload, showDocumentTypeDropdown, hideDocumentTypeDropdown] =
        useStateWithDerivedBoolean<File>(null);

    const { doc_types: docTypes } = useUploadDocumentOptions({ objectType, objectId });
    const canUploadDocuments = docTypes.length > 0;

    const docTypeState = useInputState(docTypes.length > 0 ? docTypes[0].code : '', {
        required: true,
    });
    const { checkValid } = useJointInputState([docTypeState]);

    const uploadDocument = useUploadDocument({ objectType, objectId });
    const onCancel = () => {
        setFileToUpload(null);
        hideDocumentTypeDropdown();
        setIsEditing(false);
    };

    if (!canView) {
        return null;
    }
    return (
        <VerticalStack spacing={0}>
            <HorizontalStack justifyContent={'space-between'}>
                <HeadingCaptionLabel>{captionLabel}</HeadingCaptionLabel>
                {canEdit ? (
                    <Box>
                        {isEditing ? (
                            <LeftRailTextButton onClick={onCancel}>Cancel</LeftRailTextButton>
                        ) : (
                            <LeftRailTextButton onClick={() => setIsEditing(true)}>
                                Edit
                            </LeftRailTextButton>
                        )}
                    </Box>
                ) : null}
            </HorizontalStack>

            <Box display={documents.length > 0 ? 'flex' : 'none'} flexWrap={'wrap'} gap={1}>
                {_.sortBy(documents, 'created_at').map((document) => (
                    <AxleChip
                        key={document.id}
                        icon={<FileIcon />}
                        label={renderDocumentName({ document, timezone: null })}
                        link={document.downloadLink}
                        sx={{
                            backgroundColor: 'transparent',
                            justifyContent: 'flex-start',
                            gap: 1,
                        }}
                    />
                ))}
            </Box>

            {documents.length === 0 && !isEditing ? <LeftRailNoneLabel /> : null}

            {isEditing ? (
                <VerticalStack spacing={2}>
                    <AxleDropzone
                        sx={{
                            fontSize: '14px',
                            lineHeight: '20px',
                            backgroundColor: 'transparent',
                            textAlign: 'left',
                            borderRadius: '8px',
                            padding: '8px 14px',
                            '&:hover': {
                                backgroundColor: '#2E5D5B',
                                color: '#ffffff',
                            },
                        }}
                        onUpload={(files) => setFileToUpload(files[0])}
                        showFilesList={true}
                    />
                    {showDocumentTypeDropdown && !!fileToUpload && canUploadDocuments ? (
                        <VerticalStack spacing={1}>
                            <AxleSelectField
                                label={'Document Type'}
                                inlineLabel={true}
                                inputState={docTypeState}
                                options={docTypes}
                                optionToValue={(docType) => docType.code}
                                optionToLabel={(docType) => docType.name}
                            />
                            <Button
                                disabled={!checkValid()}
                                variant='outlined'
                                onClick={() => {
                                    void uploadDocument({
                                        docFile: fileToUpload,
                                        docType: docTypeState.value,
                                    });
                                    setFileToUpload(null);
                                    hideDocumentTypeDropdown();
                                }}
                            >
                                Upload
                            </Button>
                        </VerticalStack>
                    ) : null}
                </VerticalStack>
            ) : null}
        </VerticalStack>
    );
};
